import styled from "@emotion/styled";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import type { FC } from "react";
import React, { useEffect, useRef, useState } from "react";
import { ArrowLeftIcon, IconCrossXCloseMissed } from "src/constants/icons";
import { APP_BREAKPOINT } from "src/constants/layout";
import StatusBarBloc from "src/hybrid/components/StatusBarBloc";
import { isPlatform } from "src/lib/platform";
import { useBloc } from "src/state/state";
import getPresentingElement from "../InDialog/getPresentingElement";
import { useMediaQuery } from "@react-hooks-library/core";

const Modal = styled(IonModal)`
  --background: var(--color-cream);
  --status-bar-top: 0px;
  --backdrop-opacity: 0.5;

  @media (min-width: ${APP_BREAKPOINT}px) {
    --height: max(60vh, 520px);
    --width: min(90vw, 540px);
    --border-radius: 12px;
  }

  ion-toolbar {
    --background: var(--color-cream);
    --padding-start: 0.4rem;
    --padding-end: 0.4rem;

    @media (max-width: ${APP_BREAKPOINT - 1}px) {
      --padding-start: 1rem;
      --padding-end: 1rem;
    }
  }

  ion-content {
    --background: var(--color-cream);
    --padding-bottom: calc(var(--safe-area-inset-bottom, 0px) + 12rem);

    @media (min-width: ${APP_BREAKPOINT}px) {
      --padding-bottom: calc(var(--safe-area-inset-bottom, 0px) + 6rem);
    }
  }

  @media (max-width: ${APP_BREAKPOINT - 1}px) {
    ion-content {
      --padding-start: 1.6rem;
      --padding-end: 1.6rem;
    }
  }

  &.hide-header-mobile {
    @media (max-width: ${APP_BREAKPOINT - 1}px) {
      ion-header {
        display: none;
      }

      ion-content {
        --padding-start: 0;
        --padding-end: 0;
        --padding-top: 0;
      }
    }
  }

  ion-title {
    font-size: 1rem;
    font-weight: 500;
  }

  .btn-icon {
    svg {
      width: 1.5rem;
      height: auto;
      color: var(--color-charcoal);
      display: block;
    }
  }
`;

interface ProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  title?: string;
  children?: React.ReactNode;
  hideHeaderOnMobile?: boolean;
}

const ProfileDialog: FC<ProfileDialogProps> = ({
  isOpen,
  onClose,
  onBack,
  title,
  children,
  hideHeaderOnMobile
}) => {
  const isMobile = useMediaQuery(`(max-width: ${APP_BREAKPOINT}px)`);
  const [{ title: statusBarTitle }] = useBloc(StatusBarBloc);
  const modalRef = useRef<HTMLIonModalElement>(null);

  const [presentingElement, setPresentingElement] = useState<
    HTMLElement | undefined
  >(undefined);

  useEffect(() => {
    if (isOpen && isMobile) {
      setPresentingElement(getPresentingElement());
    }
  }, [isOpen, isMobile]);

  useEffect(() => {
    const shouldApplyProfileDialogClass =
      isOpen && isMobile && presentingElement?.id === "ion-page-router";

    document.body.classList.toggle(
      "profile-dialog-open",
      shouldApplyProfileDialogClass
    );

    return () => {
      document.body.classList.remove("profile-dialog-open");
    };
  }, [isOpen, isMobile, presentingElement]);

  // Fallback cleanup when the component unmounts mid-presentation
  // (e.g. route change while modal is open). Ionic adds backdrop-no-scroll
  // to document.body and applies card-modal transforms via Web Animations.
  useEffect(() => {
    return () => {
      const el = document.getElementById("ion-page-router");
      if (el) {
        el.getAnimations().forEach((anim) => anim.cancel());
        el.style.removeProperty("transform");
        el.style.removeProperty("border-radius");
        el.style.removeProperty("overflow");
        el.style.removeProperty("transition");
      }
      document.body.classList.remove("backdrop-no-scroll");
    };
  }, []);

  const handleDidDismiss = () => {
    setPresentingElement(undefined);
  };

  return (
    <Modal
      ref={modalRef}
      isOpen={isOpen}
      presentingElement={presentingElement}
      mode={isPlatform("android") ? "md" : "ios"}
      backdropDismiss
      onWillDismiss={onClose}
      onDidDismiss={handleDidDismiss}
      className={
        isMobile && hideHeaderOnMobile ? "hide-header-mobile" : undefined
      }
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {onBack && (
              <IonButton
                aria-label="Back"
                onClick={onBack}
                className="btn-icon"
              >
                <ArrowLeftIcon aria-hidden="true" />
              </IonButton>
            )}
          </IonButtons>

          {(title ?? (onBack ? statusBarTitle : undefined)) && (
            <IonTitle>{title ?? statusBarTitle}</IonTitle>
          )}

          <IonButtons slot="end" onClick={onClose}>
            {!onBack && (
              <IonButton
                aria-label="Close"
                onClick={onClose}
                className="btn-icon"
              >
                <IconCrossXCloseMissed aria-hidden="true" />
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className={isMobile ? undefined : "ion-padding"}>
        {children}
      </IonContent>
    </Modal>
  );
};

export default ProfileDialog;
